import { Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import PendingIcon from '@mui/icons-material/PendingOutlined';
import {
  ApprovalStatus,
  Approved,
  Denied,
  EndDate,
  Pending,
  RecruiterApproved,
} from 'app/models/Placement/PlacementMods';
import recruiterSvg from 'app/assets/images/recruiterApproved.svg';
import recruiterHighlighted from 'app/assets/images/recruiterHighlighted.svg';
import Icon from '@mui/material/Icon';
import VoidIcon from '@mui/icons-material/RemoveCircleOutline';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentModDetails,
  selectIsEditExistingMod,
  selectIsEditMod,
  selectIsEmiLmoDisabled,
  selectIsApprovalStatusEditable,
} from 'store/redux-store/placement-mods/selectors';
import { selectUser } from 'oidc/user.selectors';
import { isClientContractAdminUser, isRecruitmentUser, userRoles } from 'oidc/userRoles';
import { getModName } from '../../../ModificationsUtils';
import { Brand } from 'app/components/Placement/PlacementDetails/PlacementDetailsHeader/utils';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { selectPlacementHeaderData } from 'store/redux-store/placement-details/selectors';
import { Authorized } from 'oidc/userHelper';

const approvalStatusStyle = makeStyles<{ currentSelected: string; isVoid: boolean }>()(
  (theme, { currentSelected, isVoid }) => ({
    gridContainers: {
      maxHeight: 'calc(100vh - 128px)',
      height: '100%',
      padding: '16px 24px 30px 20px',
    },
    labelStyle: {
      textAlign: 'left',
      fontFamily: 'Roboto',
      fontSize: '12px',
      fontWeight: 'bold',
      color: '#333333',
    },
    pendingBtn: {
      color: currentSelected === Pending ? '#F28D17' : '#555555',
      fontSize: '12px',
      fontWeight: 'bold',
      '&:hover': {
        color: '#F28D17',
        backgroundColor: 'transparent',
      },
      '& .MuiButton-startIcon': {
        marginRight: '3px',
      },
    },
    alignBtn: {
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
    deniedBtn: {
      color: isVoid ? '#0F6FB9' : currentSelected === Denied ? '#CC0000' : '#555555',
      fontSize: '12px',
      fontWeight: 'bold',
      '&:hover': {
        color: isVoid ? '#0F6FB9' : '#CC0000',
        backgroundColor: 'transparent',
      },
      '& .MuiButton-startIcon': {
        marginRight: '3px',
      },
    },
    approvedBtn: {
      color: isVoid
        ? '#0F6FB9'
        : currentSelected === Approved
        ? theme.palette.components.button.text.approved.color
        : '#555555',
      fontSize: '12px',
      fontWeight: 'bold',
      '&:hover': {
        color: isVoid ? '#0F6FB9' : theme.palette.components.button.text.approved.color,
        backgroundColor: 'transparent',
      },
      '& .MuiButton-startIcon': {
        marginRight: '3px',
      },
    },
    recruiterApprovedBtn: {
      color: currentSelected === RecruiterApproved ? '#F28D17' : '#555555',
      fontSize: '12px',
      fontWeight: 'bold',
      '&:hover': {
        color: '#F28D17',
        backgroundColor: 'transparent',
      },
      '& .MuiButton-startIcon': {
        marginRight: '3px',
      },
    },
    root: {
      width: '265px',
    },
    statusMargin: {
      marginTop: '12px',
      width: '265px',
    },
  }),
);

export const Recruiter = (
  <Icon>
    <img src={recruiterSvg} style={{ marginBottom: '22px' }} />
  </Icon>
);

export const RecruiterHighlighted = (
  <Icon>
    <img src={recruiterHighlighted} style={{ marginBottom: '22px' }} />
  </Icon>
);

export const ApprovalStatusSection = dialogProps => {
  const { t } = useTranslation();
  const {
    currentModDetails,
    selectedButton,
    setSelectedButtons,
    undoButtonChanges,
    setUndoButtonChanges,
    isDirty,
    modsDialogDetails,
    selectedRowDetails,
  } = dialogProps;
  const [currentSelected, setCurrentSelected] = useState(getApprovalStatus());
  const [existingStatus, setExistingStatus] = useState(getApprovalStatus());
  const { classes } = approvalStatusStyle({ currentSelected, isVoid: currentModDetails?.voidStatus });
  const [hover, setHover] = useState(false);
  const isModEdit = useSelector(selectIsEditMod);
  const appUser = useSelector(selectUser);
  const isEditExistingMod = useSelector(selectIsEditExistingMod);
  const selectedModsDetails = useSelector(selectCurrentModDetails);
  const placementDetails = useSelector(selectPlacementHeaderData);
  const [isEndDateMod, setEndDateMod] = useState(modsDialogDetails.selectedMod?.replace(/\s/g, '') === EndDate);
  const recruiterApproved = 'Recruiter Approved';
  const pending = 'Pending';
  const dispatch = useDispatch();
  const isEmiLmoDisabled = useSelector(selectIsEmiLmoDisabled);
  const isApprovalStatusEditable = useSelector(selectIsApprovalStatusEditable);

  // Helper function to determine if the modification is a bonus type
  const isBonusModification = () => {
    const selectedMod = modsDialogDetails?.selectedMod?.replaceAll(/['/.\s]/g, '');
    return selectedMod === 'Bonus';
  };

  const isDisableApprovalInLessBooking = (id: number, appprovalStatus: string) => {
    const modName = selectedRowDetails?.type?.replaceAll(/['/.\s]/g, '');
    if (
      currentModDetails?.isBookingApprovedOrGreater === false &&
      currentModDetails?.standard &&
      !isDirty &&
      currentModDetails?.approvalStatusId !== id &&
      modName !== 'Bonus'
    ) {
      return true;
    } else if (
      isDirty &&
      isEditExistingMod &&
      currentModDetails?.isBookingApprovedOrGreater === false &&
      !Authorized(
        [userRoles.candidate_Contracts_Leadership, userRoles.candidate_Contracts_TeamMember],
        appUser?.userInfo,
      ) &&
      (currentModDetails?.approvalStatusId === ApprovalStatus.Approved ||
        currentModDetails?.approvalStatusId === ApprovalStatus.Denied) &&
      currentModDetails?.approvalStatusId === id &&
      modName !== 'BillRate' &&
      modName !== 'BillingShiftDifferential' &&
      modName !== 'Preferences' &&
      modName !== 'Bonus'
    ) {
      return true;
    } else if (
      isDirty &&
      isEditExistingMod &&
      currentModDetails?.isBookingApprovedOrGreater === false &&
      !isClientContractAdminUser(appUser?.userInfo?.roles || []) &&
      (currentModDetails?.approvalStatusId === ApprovalStatus.Approved ||
        currentModDetails?.approvalStatusId === ApprovalStatus.Denied) &&
      currentModDetails?.approvalStatusId === id &&
      modName === 'BillRate' &&
      modName === 'BillingShiftDifferential' &&
      modName !== 'Preferences' &&
      modName !== 'Bonus'
    ) {
      return true;
    } else {
      return false;
    }
  };

  const approvalStatusButtons = [
    {
      id: Approved,
      component: (
        <Button
          variant="text"
          className={classes.approvedBtn}
          disabled={
            selectedButton?.isDelete ||
            selectedButton?.isVoid ||
            isDisableApprovalInLessBooking(ApprovalStatus.Approved, Approved) ||
            isEmiLmoDisabled
          }
          startIcon={currentModDetails?.voidStatus ? <VoidIcon /> : <DoneIcon />}
          onClick={e => {
            if (isBonusModification() && !isApprovalStatusEditable) {
              return; // Ignore click for bonus modifications when not editable
            }
            setCurrentSelected(Approved);
          }}
        >
          {currentModDetails?.approvalStatusId === ApprovalStatus.Approved
            ? currentModDetails?.voidStatus
              ? `${t('placement.placementMods.voidApproved')}`
              : `${t('placement.placementMods.approved')}`
            : currentSelected === Approved
            ? `${t('placement.placementMods.approved')}`
            : `${t('placement.placementMods.approve')}`}
        </Button>
      ),
    },
    {
      id: Pending,
      component: (
        <Button
          variant="text"
          className={classes.pendingBtn}
          disabled={
            selectedButton?.isDelete ||
            selectedButton?.isVoid ||
            isDisableApprovalInLessBooking(ApprovalStatus.Pending, Pending) ||
            isEmiLmoDisabled
          }
          startIcon={<PendingIcon />}
          onClick={e => {
            if (isBonusModification() && !isApprovalStatusEditable) {
              return; // Ignore click for bonus modifications when not editable
            }
            setCurrentSelected(Pending);
          }}
        >
          {t('placement.placementMods.pending')}
        </Button>
      ),
    },
    {
      id: Denied,
      component: (
        <Button
          className={classes.deniedBtn}
          disabled={
            selectedButton?.isDelete ||
            selectedButton?.isVoid ||
            isDisableApprovalInLessBooking(ApprovalStatus.Denied, Denied) ||
            isEmiLmoDisabled
          }
          variant="text"
          startIcon={currentModDetails?.voidStatus ? <VoidIcon /> : <CloseIcon />}
          onClick={e => {
            if (isBonusModification() && !isApprovalStatusEditable) {
              return; // Ignore click for bonus modifications when not editable
            }
            setCurrentSelected(Denied);
          }}
        >
          {currentModDetails?.approvalStatusId === ApprovalStatus.Denied
            ? currentModDetails?.voidStatus
              ? `${t('placement.placementMods.voidDenied')}`
              : `${t('placement.placementMods.denied')}`
            : currentSelected === Denied
            ? `${t('placement.placementMods.denied')}`
            : `${t('placement.placementMods.deny')}`}
        </Button>
      ),
    },
    {
      id: RecruiterApproved,
      component: (
        <Button
          className={classes.recruiterApprovedBtn}
          disabled={
            selectedButton?.isDelete ||
            selectedButton?.isVoid ||
            isDisableApprovalInLessBooking(ApprovalStatus.RecruiterApproved, RecruiterApproved) ||
            isEmiLmoDisabled
          }
          variant="text"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          startIcon={currentSelected === RecruiterApproved || hover ? RecruiterHighlighted : Recruiter}
          onClick={e => {
            if (isBonusModification() && !isApprovalStatusEditable) {
              return; // Ignore click for bonus modifications when not editable
            }
            setCurrentSelected(RecruiterApproved);
          }}
        >
          {t('placement.placementMods.recruiterApproved')}
        </Button>
      ),
    },
  ];

  const [acceptanceStatusOptions, setApprovalStatusOptions] = useState(getApprovalStatusOptions());

  function getApprovalStatus() {
    switch (currentModDetails?.approvalStatusId) {
      case 1:
        return Pending;
      case 2:
        return Approved;
      case 3:
        return Denied;
      case 4:
        return RecruiterApproved;
    }
  }

  function getApprovalStatusOptions() {
    const approvalOptions = [];
    const modName = selectedRowDetails?.type?.replaceAll(/['/.\s]/g, '');

    currentModDetails.approvalStatusOptions?.forEach(el => {
      const arr = approvalStatusButtons.filter(val => val.id?.replace(/ /g, '') === el.value?.replace(/ /g, ''))[0];
      if (arr) approvalOptions.push(arr);
    });

    if (
      !Authorized(
        [userRoles.candidate_Contracts_Leadership, userRoles.candidate_Contracts_TeamMember],
        appUser?.userInfo,
      ) &&
      currentModDetails?.approvalStatusId === ApprovalStatus.Pending &&
      !isEndDateMod &&
      modName !== 'BillRate' &&
      modName !== 'BillingShiftDifferential'
    ) {
      return [];
    }

    if (!isModEdit && !isEditExistingMod) {
      return [];
    }

    if (
      isEndDateMod &&
      isRecruitmentUser(appUser?.userInfo?.roles || []) &&
      placementDetails?.brandId === Brand.AMN_Labor_Disruption &&
      (isEditExistingMod || isModEdit) &&
      currentModDetails?.approvalStatusId === ApprovalStatus.Pending
    ) {
      return approvalOptions?.filter(val => val.id !== recruiterApproved);
    }

    if (isEndDateMod && isRecruitmentUser(appUser?.userInfo?.roles || []) && isModEdit && !isEditExistingMod) {
      return approvalOptions?.filter(val => val.id === recruiterApproved);
    }

    if (
      modName === 'Timeoff' &&
      selectedModsDetails?.isBookingApprovedOrGreater === false &&
      isEditExistingMod &&
      (selectedModsDetails?.approvalStatusId === 2 || selectedModsDetails?.approvalStatusId === 3)
    ) {
      return [];
    }

    if (
      (modName === 'Shift' || modName === 'Preferences') &&
      selectedModsDetails?.isBookingApprovedOrGreater === false &&
      isEditExistingMod &&
      selectedModsDetails?.approvalStatusId === 2
    ) {
      return approvalOptions?.filter(val => val.id === 'Approved');
    }
    if (
      (modName === 'Shift' || modName === 'Preferences') &&
      selectedModsDetails?.isBookingApprovedOrGreater === false &&
      isEditExistingMod &&
      selectedModsDetails?.approvalStatusId === 3
    ) {
      return approvalOptions?.filter(val => val.id === 'Denied');
    }
    if (modName === 'BillRate' || modName === 'BillingShiftDifferential') {
      if (isClientContractAdminUser(appUser?.userInfo?.roles || [])) {
        return approvalOptions?.filter(val => val.id !== recruiterApproved);
      } else {
        return [];
      }
    }
    if (
      currentModDetails.isBookingApprovedOrGreater === false &&
      !Authorized(
        [userRoles.candidate_Contracts_Leadership, userRoles.candidate_Contracts_TeamMember],
        appUser?.userInfo,
      ) &&
      currentModDetails?.approvalStatusId !== 4 &&
      currentModDetails?.approvalStatusId !== 1
    ) {
      return approvalOptions?.filter(val => val.id === 'Pending');
    } else if (isEditExistingMod || isModEdit) {
      if (
        isEndDateMod &&
        isRecruitmentUser(appUser?.userInfo?.roles || []) &&
        isEditExistingMod &&
        currentModDetails?.approvalStatusId === 1
      ) {
        return approvalOptions?.filter(val => val.id === recruiterApproved);
      } else if (
        Authorized(
          [userRoles.candidate_Contracts_Leadership, userRoles.candidate_Contracts_TeamMember],
          appUser?.userInfo,
        )
      ) {
        if (isEndDateMod && (isEditExistingMod || isModEdit)) {
          return approvalOptions?.filter(val => val.id !== recruiterApproved);
        } else {
          return approvalOptions;
        }
      } else {
        if (
          isEndDateMod &&
          currentModDetails?.approvalStatusId === 4 &&
          isRecruitmentUser(appUser?.userInfo?.roles || [])
        ) {
          return approvalOptions?.filter(val => val.id === 'Denied');
        } else {
          return approvalOptions?.filter(val => val.id === pending);
        }
      }
    } else {
      return approvalOptions?.filter(val => val.id !== recruiterApproved);
    }
  }

  const setInitialValues = () => {
    setCurrentSelected(getApprovalStatus());
    setExistingStatus(getApprovalStatus());
    setApprovalStatusOptions(getApprovalStatusOptions());
    setUndoButtonChanges(false);
  };

  useEffect(() => {
    setApprovalStatusOptions(getApprovalStatusOptions());
  }, [
    currentSelected,
    selectedButton?.isDelete,
    selectedButton?.isVoid,
    hover,
    currentModDetails,
    isDirty,
    isModEdit,
    isEditExistingMod,
    isApprovalStatusEditable,
  ]);

  useEffect(() => {
    if (currentModDetails && undoButtonChanges) {
      setInitialValues();
    } else if (currentModDetails) {
      setInitialValues();
    }
  }, [currentModDetails, undoButtonChanges]);

  useEffect(() => {
    if (currentSelected) {
      setSelectedButtons({ ...selectedButton, approvedStatus: ApprovalStatus[currentSelected.replace(/ /g, '')] });
    }
    if (currentSelected === Approved) {
      dispatch(placementModsAction.setApprovalStatusSelected(true));
    } else {
      dispatch(placementModsAction.setApprovalStatusSelected(false));
    }
  }, [currentSelected]);
  useEffect(() => {
    if (
      isEditExistingMod &&
      currentModDetails?.isBookingApprovedOrGreater === false &&
      isDirty &&
      (currentModDetails?.approvalStatusId === ApprovalStatus.Approved ||
        currentModDetails?.approvalStatusId === ApprovalStatus.Denied ||
        currentModDetails?.approvalStatusId === ApprovalStatus.RecruiterApproved)
    ) {
      //*For preferences mod  change appproval status selection
      if (
        getModName(selectedRowDetails) === 'Preferences' ||
        (currentModDetails?.standard && getModName(selectedRowDetails) === 'Bonus')
      ) {
        setCurrentSelected(Approved);
      } else {
        setCurrentSelected(Pending);
      }
    }
  }, [isDirty]);

  return (
    <div
      style={selectedButton?.isDelete || selectedButton?.isVoid ? { opacity: 0.5 } : null}
      className={classes.statusMargin}
    >
      <label className={classes.labelStyle}>{t('placement.placementMods.approvalStatus') + ':'}</label>
      {approvalStatusButtons
        .filter(btn => btn.id === existingStatus)
        .map((btn, index) => {
          return <React.Fragment key={index}>{btn.component}</React.Fragment>;
        })}
      <div className={classes.alignBtn}>
        {acceptanceStatusOptions?.map((btn, index) => {
          return <React.Fragment key={index}>{btn.component}</React.Fragment>;
        })}
      </div>
    </div>
  );
};
